import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { NumericFormat } from 'react-number-format';
import Button from '../../../composants/Button/Button';
import { PrintButton } from '../../../composants/PrintButton/PrintButton';
import NextTabButton from '../../../composants/NextTabButton/NextTabButton';
import ValidationPopUp from '../../../composants/ValidationPopUp/ValidationPopUp';
import SignatureFlow from '../../../composants/Marche/SignatureFlow';
import SectionPliable from '../../../composants/SectionPliable/SectionPliable';
import AttestationDocuments from '../../../composants/AttestationDocuments/AttestationDocuments';
import { getStatusColor } from '../../../utils/entityUtils';
import { CommandeMS, TabKind } from '../types';
import { ctaVersNotifications } from '../domain/commandeMsNextTab';
import {
  CandidatureView,
  CandidatureErrors,
  CritereReponses,
  deriveCandidatureView,
  totalHT,
  validateCandidature,
  buildCandidaterPayload,
  isCandidatureExpired,
  isCandidatureEditable,
  canRefuserConsultation,
  isConsultationEnCours,
  dateLimiteRemiseOffres,
  depotOffre,
} from '../domain/commandeMsCandidature';
import { useChronoExpired } from '../hooks/useChronoExpired';
import Banner from '../../../composants/Banner/Banner';
import {
  candidaterCommande,
  refuserConsultation,
  saveBrouillonCandidature,
  buildCandidaterSignerUrl,
  getMsSignatureMode,
  updateAttestationDocuments,
} from '../services/commandeMsService';
import './CommandeMsCandidature.css';

// Forme minimale des valeurs renvoyees par NumericFormat (evite un import de type instable).
interface NumberFormatValues {
  formattedValue: string;
  value: string;
  floatValue?: number;
}

interface CommandeMsCandidatureProps {
  commande: CommandeMS;
  // Bascule d'onglet par `kind` (fournie par le shell) : "Accéder au marché signé" -> Notifications.
  goToTab?: (kind: TabKind) => void;
}

// Onglet "Candidature" de la vue PRESTATAIRE commande-MS (V2). Fork autonome de l'ancien composant
// partage Candidature.tsx : etat React local (zero mutation de prop), id en argument (zero
// window.location), gating 100% id-based via le domaine, erreurs en modale/inline (zero alert),
// signature OTP via le widget partage SignatureFlow (resourcePrefix="subsequents/prestataire").
//
// Parite differee (charte §5, signalee, non silencieuse) : le bouton d'impression PDF de l'oracle
// (PrintButton + suiviHeaderProps) n'est pas reproduit ici — l'impression du suivi sera recablee
// au niveau du shell quand on traitera l'impression de la filiere presta.
const CommandeMsCandidature: React.FC<CommandeMsCandidatureProps> = ({ commande, goToTab }) => {
  const navigate = useNavigate();

  // Expiration "vivante" : le chrono partage emet 'chronoExpired' a zero.
  const chronoExpired = useChronoExpired();

  // Vue-modele editable, (re)derivee a chaque changement de commande (pas de mutation de la prop).
  const [view, setView] = useState<CandidatureView>(() => deriveCandidatureView(commande));
  useEffect(() => {
    setView(deriveCandidatureView(commande));
  }, [commande]);

  const [errors, setErrors] = useState<CandidatureErrors>({ criteres: {}, prix: {} });
  const [prixValidated, setPrixValidated] = useState(false);

  // Attestation "documents administratifs a jour" (regle metier marches publics : l'acheteur engage
  // la commande sur la foi de documents valides). Etat coche initial = reflet de
  // attestation_documents_at (regle 6 : cochee si deja atteste), re-derive si la commande change.
  // attestationError porte l'obligation posee A L'ENVOI (miroir front de la garde back 422).
  const [attesteDocuments, setAttesteDocuments] = useState(!!commande.attestation_documents_at);
  const [attestationError, setAttestationError] = useState<string | null>(null);
  useEffect(() => {
    setAttesteDocuments(!!commande.attestation_documents_at);
  }, [commande.attestation_documents_at]);

  // Modales
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [refuserOpen, setRefuserOpen] = useState(false);
  const [refusSuccessOpen, setRefusSuccessOpen] = useState(false);
  const [errorModal, setErrorModal] = useState<string | null>(null);

  // Signature manuscrite presta (OTP). Le mode (manuscrite/automatique) vient de /app/signature-mode.
  const [signatureMode, setSignatureMode] = useState<'automatique' | 'manuscrite'>('automatique');
  const [showSignModal, setShowSignModal] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<Record<string, unknown> | null>(null);
  useEffect(() => {
    getMsSignatureMode().then(setSignatureMode).catch(() => {});
  }, []);

  const expired = isCandidatureExpired(commande, chronoExpired);
  const editable = isCandidatureEditable(commande, chronoExpired);
  const canRefuser = canRefuserConsultation(commande, chronoExpired);
  const disabled = !editable;
  // Bandeau de rappel du delai : uniquement pendant la consultation.
  const dateLimite = isConsultationEnCours(commande, chronoExpired) ? dateLimiteRemiseOffres(commande) : null;
  // Bandeau de confirmation : qui a depose l'offre, et quand.
  const depot = depotOffre(commande);
  // CTA de bascule vers Notifications et signatures. En pratique c'est le SEUIL D'ATTEIGNABILITE de
  // l'onglet destination qui le tient a l'ecart de l'orange "Soumettre l'offre" : Notifications
  // n'ouvre qu'a partir du statut 8, l'offre n'est editable qu'en dessous de 4 — les deux barres ne
  // coexistent jamais. La garde `isCandidatureEditable` du domaine nomme la regle metier, elle ne
  // declenche pas.
  const ctaNotifications = ctaVersNotifications(commande, chronoExpired);

  // Impression de l'offre deposee : le ref cible les sections (bandeaux et actions restent dehors).
  const printRef = useRef<HTMLDivElement>(null);
  const lotTechnique = commande.operation?.lot_technique;
  const printSubtitle = lotTechnique
    ? `${lotTechnique.id} - ${lotTechnique.nom}, ${commande.operation?.id_lot_departement ?? ''} - ${
        commande.operation?.lot_departement?.departement?.nom ?? ''
      }`
    : 'Informations de lot non disponibles';
  const total = useMemo(() => totalHT(view.lignes), [view.lignes]);

  // --- Saisie -----------------------------------------------------------------
  const setReponse = (value: string) => {
    setView((prev) => ({ ...prev, reponseTechnique: value }));
    setErrors((prev) => ({ ...prev, reponseTechnique: undefined }));
  };

  const setCritere = (key: keyof CritereReponses, value: string) => {
    setView((prev) => ({ ...prev, criteres: { ...prev.criteres, [key]: value } }));
    setErrors((prev) => ({ ...prev, criteres: { ...prev.criteres, [key]: undefined } }));
  };

  const setPrix = (index: number, value: number) => {
    setView((prev) => {
      const lignes = [...prev.lignes];
      if (lignes[index]) lignes[index] = { ...lignes[index], prixHT: value };
      return { ...prev, lignes };
    });
    if (value > 0) {
      setErrors((prev) => {
        const prix = { ...prev.prix };
        delete prix[index];
        return { ...prev, prix };
      });
    }
  };

  // --- Actions ----------------------------------------------------------------
  const handleSaveDraft = async () => {
    try {
      await saveBrouillonCandidature(commande.id, buildCandidaterPayload(view, commande));
      navigate('/commandes');
    } catch (err) {
      setErrorModal(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde de la candidature');
    }
  };

  const handleConfirmSubmit = () => {
    setPrixValidated(true);
    const { valid, errors: nextErrors } = validateCandidature(view);
    setErrors(nextErrors);

    // Garde attestation, cote a cote avec la validation de l'offre (meme forme que la garde
    // 'offre technique' du module BC : booleen coche, erreur locale, scroll, blocage de l'envoi).
    // L'attestation documents administratifs est OBLIGATOIRE avant de deposer l'offre ; un champ
    // obligatoire muet rendrait le bouton "sans effet" (piege connu du repo), d'ou l'erreur posee.
    const attestationOk = attesteDocuments;
    if (!attestationOk) {
      setAttestationError(
        'Vous devez attester que vos documents administratifs sont à jour avant de déposer votre offre.',
      );
    }

    if (valid && attestationOk) {
      setConfirmOpen(true);
      return;
    }
    // Amene le premier defaut a l'ecran (parite oracle l.379-392, etendue a l'attestation). setTimeout :
    // laisser le rendu poser les classes .error / le message d'attestation avant de cibler (meme
    // precaution que la garde 'offre technique' cote BC). Priorite : prix manquant -> table des prix,
    // sinon premier champ texte en erreur, sinon la case d'attestation.
    window.setTimeout(() => {
      const target =
        Object.keys(nextErrors.prix).length > 0
          ? document.querySelector('.articles-table')
          : document.querySelector('.error, .reponse-technique-error, .attestation-documents-error');
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const handleConfirmed = async () => {
    setConfirmOpen(false);
    const payload = buildCandidaterPayload(view, commande);
    // Persiste l'attestation (horodatage back = preuve datee de l'engagement, regle 5) AVANT tout
    // depot : la garde back du candidater refuse (422) tant que attestation_documents_at est null.
    // On le fait ici, au moment exact de l'engagement, pour les DEUX modes — y compris le manuscrit,
    // ou le POST final .../candidater part de SignatureFlow apres l'OTP (donc l'attestation doit deja
    // etre en base a ce moment-la). En echec, on ne lance pas le depot (evite un 422 opaque).
    try {
      await updateAttestationDocuments(commande.id, true);
    } catch (err) {
      setErrorModal(
        err instanceof Error ? err.message : "Erreur lors de l'enregistrement de l'attestation",
      );
      return;
    }
    // Mode manuscrite : on passe par SignatureFlow (OTP + dessin), qui POST sur .../candidater.
    if (signatureMode === 'manuscrite') {
      setPendingPayload(payload as unknown as Record<string, unknown>);
      setShowSignModal(true);
      return;
    }
    // Mode automatique : soumission directe.
    try {
      await candidaterCommande(commande.id, payload);
      navigate('/commandes');
    } catch (err) {
      setErrorModal(err instanceof Error ? err.message : 'Erreur lors de la soumission de la candidature');
    }
  };

  const handleRefuser = async () => {
    setRefuserOpen(false);
    try {
      await refuserConsultation(commande.id);
      setRefusSuccessOpen(true);
    } catch (err) {
      setErrorModal(err instanceof Error ? err.message : 'Erreur lors du refus de la consultation');
    }
  };

  const critereFields: { key: keyof CritereReponses; label: string; placeholder: string }[] = [
    {
      key: 'organisation',
      label: "Modes Opératoires pour l'éxecution du marché",
      placeholder: 'Décrivez votre organisation pour ce marché',
    },
    {
      key: 'moyens',
      label: 'Moyens Humains et Materiels dédiés au marché',
      placeholder: 'Décrivez les moyens que vous mobilisez',
    },
    {
      key: 'social',
      label: 'Mesures en matière Environnementale & Sociale',
      placeholder: 'Décrivez vos engagements sociaux et environnementaux',
    },
  ];

  // Bandeaux d'etat, rendus EN HAUT et EN BAS de l'onglet (juste avant les actions) : l'onglet est
  // long, le prestataire ne doit pas avoir a remonter pour retrouver le delai ou la confirmation.
  const bandeauxEtat = (
    <>
      {dateLimite && (
        <Banner variant="warning" message={`Vous êtes invités à déposer votre offre jusqu'au ${dateLimite}`} />
      )}
      {depot && (
        <Banner variant="success" message={`Offre déposée par ${depot.nom} le ${depot.dateHeure}`} />
      )}
    </>
  );

  return (
    <div className="tab-candidature">
      {bandeauxEtat}

      {expired && (
        <div className="expired-notice">
          Cette commande est expirée. Vous ne pouvez plus soumettre de candidature ni modifier vos
          réponses.
        </div>
      )}

      {/* Descriptif Administratif retire de la Candidature : il est desormais dans l'onglet
          Consultation (lecture seule), en amont. */}

      {/* Zone imprimee : l'offre elle-meme (les bandeaux et les actions restent en dehors). */}
      <div ref={printRef}>

      {/* Offre technique : réponse + 3 critères */}
      <SectionPliable title="Offre technique">
        <div className="section-content">
          {/* Le besoin de l'acheteur n'est PAS repris ici : il vit dans l'onglet Consultation, en
              amont et en lecture seule. Le dupliquer au-dessus de la reponse noyait le seul champ
              que le prestataire a a remplir. */}
          <div className="form-group">
            <label>
              Réponse Technique au Besoin <span style={{ color: 'red' }}>*</span>
            </label>
            {/* Lecture seule (offre deposee ou operation figee) : rendu en div qui epouse la
                hauteur du texte, au lieu d'un textarea a hauteur fixe qui scrolle et tronque a
                l'impression. */}
            {disabled ? (
              <div className="input-inactif offre-reponse-lecture">
                {view.reponseTechnique.trim() ? view.reponseTechnique : 'Non renseigné'}
              </div>
            ) : (
              <textarea
                value={view.reponseTechnique}
                onChange={(e) => setReponse(e.target.value)}
                className={`definition-besoin-textarea ${errors.reponseTechnique ? 'error' : ''}`}
                placeholder={
                  expired
                    ? 'Opération expirée - Réponse non modifiable'
                    : 'Décrivez votre réponse technique au besoin exprimé'
                }
                rows={15}
              />
            )}
            {errors.reponseTechnique && (
              <span className="error-message reponse-technique-error">{errors.reponseTechnique}</span>
            )}
          </div>

          {critereFields.map((field) => (
            <div className="form-group critere-section" key={field.key}>
              <label>
                {field.label} <span className="required">*</span>
              </label>
              {disabled ? (
                <div className="input-inactif offre-reponse-lecture">
                  {view.criteres[field.key].trim() ? view.criteres[field.key] : 'Non renseigné'}
                </div>
              ) : (
                <textarea
                  value={view.criteres[field.key]}
                  onChange={(e) => setCritere(field.key, e.target.value)}
                  placeholder={expired ? 'Opération expirée - Réponse non modifiable' : field.placeholder}
                  className={errors.criteres[field.key] ? 'error' : ''}
                  rows={15}
                />
              )}
              {errors.criteres[field.key] && (
                <span className="error-message">{errors.criteres[field.key]}</span>
              )}
            </div>
          ))}
        </div>
      </SectionPliable>
      <div className="page-break" />

      {/* Offre Financière : prix HT par article */}
      <SectionPliable title="Offre financière">
        <div className="section-content">
          <table className="articles-table">
            <thead>
              <tr>
                <th className="financier-numero-article">N°</th>
                <th>Description de la prestation</th>
                <th className="financier-prix">Prix forfaitaire HT</th>
              </tr>
            </thead>
            <tbody>
              {view.lignes.map((ligne, index) => (
                <tr key={ligne.id || index} className={errors.prix[index] ? 'prix-error-cell' : ''}>
                  <td className="financier-numero-article">{ligne.code}</td>
                  <td>{ligne.nom}</td>
                  <td className="financier-prix">
                    <div className="prix-input-container">
                      <NumericFormat
                        value={ligne.prixHT || ''}
                        disabled={disabled}
                        thousandSeparator=" "
                        decimalSeparator=","
                        decimalScale={2}
                        fixedDecimalScale
                        allowNegative={false}
                        allowLeadingZeros={false}
                        placeholder="0,00"
                        className={`prix-input ${errors.prix[index] ? 'error' : ''}`}
                        isAllowed={(values: NumberFormatValues) => {
                          const { floatValue } = values;
                          return floatValue === undefined || floatValue <= 1000000000;
                        }}
                        onValueChange={(values: NumberFormatValues) => setPrix(index, values.floatValue ?? 0)}
                      />
                      <span className="prix-currency">€</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {prixValidated && Object.keys(errors.prix).length > 0 && (
            <div className="error-global-prix">
              Veuillez renseigner un prix HT valide pour tous les articles avant de soumettre votre
              candidature.
            </div>
          )}

          <div className="total-section">
            <div className="total-row">
              <span>Total DPGF HT </span>
              <span>
                {total.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </span>
            </div>
          </div>
        </div>
      </SectionPliable>
      </div>

      {/* Attestation OBLIGATOIRE "documents administratifs a jour". Placee AVANT les bandeaux d'etat,
          en PARITE avec le BC : l'ordre est corps de page -> case -> bannieres -> actions, identique
          des deux cotes. Dispositif DISTINCT des CGV. Elle n'est jamais figee en lecture seule
          (regle 6) : tant qu'elle est affichee (etat editable), elle reste interactive. Le composant
          partage gere lui-meme l'ouverture de la modale de profil sur l'onglet Documents ; on ne pilote
          que checked / onChange / error. */}
      {editable && (
        <AttestationDocuments
          checked={attesteDocuments}
          onChange={(checked) => {
            setAttesteDocuments(checked);
            if (checked) setAttestationError(null);
          }}
          entiteId={commande.id_entite}
          error={attestationError}
        />
      )}

      {/* Rappel des bandeaux d'etat en pied d'onglet, juste avant les actions. */}
      {bandeauxEtat}

      {/* Offre deposee : plus d'action de saisie, mais l'offre reste imprimable et, une fois le marche
          en phase de signature, on enchaine vers Notifications et signatures.
          La barre s'ouvre aussi sur le seul CTA : `depot` depend d'une preuve de signature enrichie
          par le core, absente de certaines donnees anciennes — la lier au CTA ferait disparaitre la
          bascule sur ces commandes-la, alors que l'onglet destination, lui, est bien atteignable. */}
      {(depot || !ctaNotifications.hidden) && (
        <div className="action-buttons no-print">
          {depot && (
            <PrintButton
              targetRef={printRef}
              fileName={`${commande.operation?.numero ?? ''} - Offre`}
              value="Imprimer l'offre"
              suiviHeaderProps={{
                titre: commande.operation?.nom ?? 'Marché subséquent',
                sousTitre: '',
                subtitle: printSubtitle,
                status: commande.status?.nom || 'Statut inconnu',
                statusColor: getStatusColor(commande.status?.nom || ''),
                delais: commande.expire_at ?? commande.operation?.expire_at ?? undefined,
              }}
            />
          )}
          <NextTabButton
            label={ctaNotifications.label}
            hidden={ctaNotifications.hidden}
            onClick={() => goToTab?.(ctaNotifications.target)}
          />
        </div>
      )}

      {/* Actions (uniquement quand la candidature est encore editable) */}
      {editable && (
        <>
          <div className="action-buttons no-print">
            {canRefuser && (
              <Button
                value="Refuser la consultation"
                variant="cancel"
                onClick={() => setRefuserOpen(true)}
              />
            )}
            <Button value="Enregistrer et quitter" variant="secondary" onClick={handleSaveDraft} />
            <Button value="Soumettre l'offre" variant="primary" onClick={handleConfirmSubmit} />
          </div>
        </>
      )}

      {/* Confirmation de dépôt */}
      <ValidationPopUp
        isOpen={confirmOpen}
        title="Dépôt de la candidature"
        text="Vous vous apprêtez à déposer votre candidature. Il n'est plus possible de modifier votre offre après cette étape. Voulez-vous continuer ?"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmed}
        cancelLabel="Non"
        confirmLabel="Oui"
      />

      {/* Confirmation de refus */}
      <ValidationPopUp
        isOpen={refuserOpen}
        title="Refuser la consultation"
        text="Vous vous apprêtez à refuser de candidater à ce marché subséquent. Cette action est irréversible."
        onClose={() => setRefuserOpen(false)}
        onConfirm={handleRefuser}
        cancelLabel="Annuler"
        confirmLabel="Refuser"
      />

      {/* Succès du refus */}
      <ValidationPopUp
        isOpen={refusSuccessOpen}
        title="Consultation refusée"
        text="La consultation a été refusée avec succès. Vous allez être redirigé vers la liste des commandes."
        onClose={() => navigate('/commandes')}
        onConfirm={() => navigate('/commandes')}
        cancelLabel=""
        confirmLabel="OK"
      />

      {/* Erreur d'action (remplace les alert() natifs) */}
      <ValidationPopUp
        isOpen={errorModal !== null}
        title="Erreur"
        text={errorModal ?? ''}
        onClose={() => setErrorModal(null)}
        onConfirm={() => setErrorModal(null)}
        cancelLabel=""
        confirmLabel="OK"
      />

      {/* Signature manuscrite presta MS (OTP) — POST .../subsequents/prestataire/signature/{id}/candidater */}
      {showSignModal && pendingPayload && (
        <SignatureFlow
          operationId={commande.id}
          commandeId={commande.id}
          resourcePrefix="subsequents/prestataire"
          signerUrl={buildCandidaterSignerUrl(commande.id)}
          additionalPayload={pendingPayload}
          successTitle="Candidature déposée"
          successBody="Votre signature a été apposée et votre candidature enregistrée."
          onClose={() => {
            setShowSignModal(false);
            setPendingPayload(null);
          }}
          onSigned={() => {
            setShowSignModal(false);
            setPendingPayload(null);
            navigate('/commandes');
          }}
        />
      )}
    </div>
  );
};

export default CommandeMsCandidature;
